import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import LeafgateLogo from "./LeafgateLogo";
import { isCognitoEnabled, cognitoResetPassword } from "../lib/cognito";
import "./../styles/login.css";

const getErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item) => item?.msg || String(item)).join(" ");
  }
  return "No se pudo restablecer la contraseña.";
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();

  // Local flow: token comes in the URL query param.
  // Cognito flow: email + verification code sent to the user's email.
  const urlToken = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const urlEmail = useMemo(() => searchParams.get("email") || "", [searchParams]);

  const [email, setEmail] = useState(urlEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (password.length < 8) {
      setErrorMessage("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Las contraseñas no coinciden.");
      return;
    }

    setIsLoading(true);
    try {
      if (isCognitoEnabled) {
        if (!email || !code) {
          setErrorMessage("Se requiere email y código de verificación.");
          setIsLoading(false);
          return;
        }
        await cognitoResetPassword(email, code, password);
        setSuccessMessage("Contraseña actualizada correctamente. Ya puedes iniciar sesión.");
      } else {
        if (!urlToken) {
          setErrorMessage("Falta el token de recuperación en el enlace.");
          setIsLoading(false);
          return;
        }
        const response = await axios.post("/v1/users/reset-password", {
          token: urlToken,
          password,
        });
        setSuccessMessage(response?.data?.message || "Contraseña actualizada correctamente.");
      }
      setPassword("");
      setConfirmPassword("");
      setCode("");
    } catch (error) {
      setErrorMessage(error.message || getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <LeafgateLogo size={32} color="#191A1F" />
          <span className="login-brand-name">ClubsAgent</span>
        </div>

        <h2 className="login-title">Restablece tu contraseña</h2>
        <p className="login-description">
          Elige una contraseña nueva para recuperar el acceso a tu cuenta.
        </p>

        <form onSubmit={handleSubmit} className="login-form">
          {errorMessage && (
            <p className="login-error" role="alert">
              {errorMessage}
            </p>
          )}
          {successMessage && (
            <p className="login-success" role="status">
              {successMessage}
            </p>
          )}

          {isCognitoEnabled && (
            <>
              <div className="login-field">
                <label htmlFor="reset-email" className="login-label">
                  Email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="login-input"
                  placeholder="Tu email"
                  autoComplete="email"
                />
              </div>
              <div className="login-field">
                <label htmlFor="reset-code" className="login-label">
                  Código de verificación
                </label>
                <input
                  id="reset-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  className="login-input"
                  placeholder="Código recibido por email"
                  autoComplete="one-time-code"
                />
              </div>
            </>
          )}

          <div className="login-field">
            <label htmlFor="password" className="login-label">
              Nueva contraseña
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="login-input"
              placeholder="Nueva contraseña"
              autoComplete="new-password"
            />
          </div>

          <div className="login-field">
            <label htmlFor="confirm-password" className="login-label">
              Confirmar contraseña
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="login-input"
              placeholder="Repite la contraseña"
              autoComplete="new-password"
            />
          </div>

          <button type="submit" disabled={isLoading} className="login-submit">
            {isLoading ? "Actualizando..." : "Guardar nueva contraseña"}
          </button>
        </form>

        <p className="login-footer">
          <Link to="/login" className="login-footer-link">
            Volver al login
          </Link>
        </p>
      </div>
    </div>
  );
}
